/** @file Home of UserInfo. */
import * as emailValidator from 'email-validator';
import { always } from 'purify-ts';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { errors } from '@codegrade/utils';
import client from '@/client';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import CgError from '@/components/Error';
import * as Form from '@/components/Form';
import { Icon } from '@/components/Icon';
import Loader from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { OTPForm } from '@/components/NewLoginAndRegister';
import PasswordSuggestions from '@/components/PasswordSuggestions';
import * as Tabs from '@/components/Tabs';
import { PASSWORD_UNIQUE_MESSAGE } from '@/constants';
import { GlobalPermissions as GPerm } from '@/permissions';
import { UserStore } from '@/store';
import * as utils from '@/utils';
const clsActionButton = 'justify-between min-h-9';
export default tsx.component({
    name: 'user-info',
    /** @returns Component initial state. */
    data() {
        return {
            error: Maybe.empty(),
            fullNameInputValue: '',
            emailInputValue: '',
            passwordInputValue: '',
            confirmPasswordInputValue: '',
            oldPasswordInputValue: '',
            emailChangeSession: Maybe.empty(),
        };
    },
    computed: {
        /** @returns Whether the logged-in user can edit their own profile info. */
        canEditInfo() {
            return UserStore.tenantId().mapOrDefault(tid => UserStore.hasTenantPermission()(GPerm.can_edit_own_info, tid), false);
        },
        /** @returns Whether the logged-in user can edit their own password. */
        canEditPw() {
            return UserStore.tenantId().mapOrDefault(tid => UserStore.hasTenantPermission()(GPerm.can_edit_own_password, tid), false);
        },
        /** @returns The currently logged-in user, or Nothing if not yet loaded. */
        loggedInUser() {
            return UserStore.loggedInUser();
        },
        /** @returns Whether the user data is still loading. */
        loading() {
            return this.loggedInUser.isNothing();
        },
        /** @returns The change email modal trigger and form node. */
        changeEmailModal() {
            if (!this.canEditInfo) {
                return utils.emptyVNode();
            }
            const email = this.loggedInUser.chain(u => u.email);
            return (<Modal class="contents" onClose={() => {
                    this.emailChangeSession = Maybe.empty();
                }} scopedSlots={{
                    trigger: mo => (<Button data-cy="change-email" semantic="actionable" prominence={1} action={() => {
                            this.emailInputValue = '';
                            this.oldPasswordInputValue = '';
                            mo.open();
                        }} buttonClasses={clsActionButton}>
                                Change Email
                                <Icon icon={icons.OpenModal}/>
                            </Button>),
                    modalHeader: always('Change email address'),
                    modalBody: mo => (<Tabs.TabGroup defaultTab="intro" selectedTab={this.emailChangeSession
                            .map(session => session.currentStep)
                            .orDefault('intro')} class="flex max-sm:flex-col gap-3">
                                <Tabs.TabList class="flex sm:flex-col gap-1 sr-only">
                                    <Tabs.TabButton tabName="intro" label="Introduction" disabled/>
                                    <Tabs.TabButton tabName="confirm-new" label="Confirm new address" disabled/>
                                </Tabs.TabList>

                                <div>
                                    <Tabs.TabPanel tabName="intro">
                                        <Form.Root data-cy="change-email" class="md:min-w-[75dvw]" handleSubmit={async () => {
                            if (!emailValidator.validate(this.emailInputValue)) {
                                throw new Error('Please make sure the new email address is correct');
                            }
                            if (this.oldPasswordInputValue === '') {
                                throw new Error('Please enter your current password');
                            }
                            const res = await client.p.emailChange.create({
                                requestBody: {
                                    newEmail: this.emailInputValue,
                                    oldPassword: this.oldPasswordInputValue,
                                },
                            });
                            return res.addCallbacks({
                                onAfterSuccess: () => {
                                    this.emailChangeSession = Maybe.of({
                                        token: res.cgResult.id,
                                        currentStep: 'confirm-new',
                                    });
                                },
                            });
                        }} submitLoadingMessage="Updating email address..." submitSuccessMessage="Email address updated" submitOnCtrlOrCommandEnter submitOnEnter submitLabel="Continue">
                                            <Form.Field label="Current email">
                                                <Form.Input data-cy="current-email" readOnly value={email.orDefault('(unknown)')}/>
                                            </Form.Field>

                                            <Form.Field label="New email">
                                                <Form.Input data-cy="new-email" type="email" value={this.emailInputValue} onInput={newVal => {
                            this.emailInputValue = newVal;
                        }} required v-focus/>
                                            </Form.Field>

                                            {this.renderCurrentPasswordNode({
                            description: 'Enter your current password to change your email address.',
                        })}
                                        </Form.Root>
                                    </Tabs.TabPanel>

                                    <Tabs.TabPanel tabName="confirm-new">
                                        <h4 class="title-medium">Confirm new email</h4>
                                        <OTPForm prefilled={Maybe.empty()} submit={async (code) => {
                            const res = await client.p.emailChange.confirmNew({
                                requestBody: { code },
                                token: this.emailChangeSession
                                    .map(s => s.token)
                                    .orDefault(''),
                            });
                            return res.addCallbacks({
                                onAfterSuccess: () => {
                                    UserStore.updateEmail({
                                        email: res.cgResult.email,
                                        emailVerified: true,
                                    });
                                    mo.close();
                                    this.emailChangeSession = Maybe.empty();
                                },
                            });
                        }}/>
                                    </Tabs.TabPanel>
                                </div>
                            </Tabs.TabGroup>),
                }}/>);
        },
        /** @returns The change password modal trigger and form node. */
        changePasswordModal() {
            if (!this.canEditInfo || !this.canEditPw) {
                return utils.emptyVNode();
            }
            return (<Modal class="contents" scopedSlots={{
                    trigger: mo => (<Button data-cy="change-password" semantic="actionable" prominence={1} action={() => {
                            this.oldPasswordInputValue = '';
                            this.passwordInputValue = '';
                            this.confirmPasswordInputValue = '';
                            mo.open();
                        }} buttonClasses={clsActionButton}>
                                Change Password
                                <Icon icon={icons.OpenModal}/>
                            </Button>),
                    modalHeader: always('Change password'),
                    modalBody: mo => (<Form.Root data-cy="change-password" class="md:min-w-[75dvw]" handleSubmit={async () => {
                            if (this.passwordInputValue !== this.confirmPasswordInputValue) {
                                throw new Error('Passwords do not match');
                            }
                            const res = await UserStore.updateUserInfo({
                                name: undefined,
                                newPw: this.passwordInputValue,
                                oldPw: this.oldPasswordInputValue,
                            });
                            return res.addCallbacks({
                                onAfterSuccess: () => {
                                    mo.close();
                                    this.oldPasswordInputValue = '';
                                    this.passwordInputValue = '';
                                    this.confirmPasswordInputValue = '';
                                },
                            });
                        }} submitLoadingMessage="Updating password..." submitSuccessMessage="Password updated" submitOnCtrlOrCommandEnter submitOnEnter scopedSlots={{
                            formError: err => err instanceof api.ApiResultError &&
                                err.error instanceof api.WeakPasswordException ? (<PasswordSuggestions error={err.error}/>) : (utils.getErrorMessage(err)),
                            actionBar: () => utils.ifJustOrEmpty(this.loggedInUser.map(u => u.tenantId), tenantId => (<router-link to={{
                                    name: 'forgot',
                                    params: { tenantId },
                                }}>
                                                    Forgot password?
                                                </router-link>)),
                        }}>
                                {this.renderCurrentPasswordNode({ autofocus: true })}
                                <Form.Field label="New password" description={PASSWORD_UNIQUE_MESSAGE}>
                                    <Form.Input data-cy="new-password" type="password" value={this.passwordInputValue} onInput={newVal => {
                            this.passwordInputValue = newVal;
                        }}/>
                                </Form.Field>
                                <Form.Field label="Confirm new password">
                                    <Form.Input data-cy="confirm-password" type="password" value={this.confirmPasswordInputValue} onInput={newVal => {
                            this.confirmPasswordInputValue = newVal;
                        }}/>
                                </Form.Field>
                            </Form.Root>),
                }}/>);
        },
        /** @returns The main profile body — a loader while fetching, or the profile card when ready. */
        bodyNode() {
            if (this.loggedInUser.isNothing()) {
                return <Loader scale={1}/>;
            }
            const { name, username, email } = this.loggedInUser.value;
            const clsProfileFieldLabel = 'label-large text-brand-60 dark:text-brand-20';
            const clsProfileFieldValue = 'label-large text-brand-50 dark:text-brand-20 m-0';
            /**
             * Renders a single labelled profile field.
             *
             * @param label The field label shown to the left.
             * @param value The field value; renders an italic placeholder when empty.
             * @returns The rendered field element.
             */
            const renderField = (label, value) => (<div data-cy={`user-${label}`} class="flex items-baseline gap-2">
                    <dt class={clsProfileFieldLabel}>{label}</dt>
                    <span class={clsProfileFieldLabel}>:</span>
                    <dd data-cy={`user-${label}-value`} class={[clsProfileFieldValue, { italic: value.isNothing() }]}>
                        {value.orDefault(`No ${label}`)}
                    </dd>
                </div>);
            return (<div data-cy="user-info" class="flex flex-col gap-3">
                    <dl data-cy="user-profile" class={['contents']}>
                        <div class="bg-brand-5 dark:bg-brand-70 rounded-2xl px-4! py-6 m-0">
                            <div class="flex flex-row gap-3 items-center-safe">
                                <Icon icon={icons.UserCircle} class="size-6 @md:size-14 shrink-0 text-brand-50 dark:text-brand-30"/>
                                <div class="flex-auto -translate-y-0.5 flex flex-col gap-0">
                                    {this.renderFullnameDisplay(name)}
                                </div>
                            </div>

                            {renderField('username', Maybe.of(username))}
                            {renderField('email', email)}
                        </div>
                    </dl>

                    <div class="flex flex-col items-stretch gap-1 empty:hidden">
                        {this.changeEmailModal}
                        {this.changePasswordModal}
                    </div>
                </div>);
        },
    },
    methods: {
        /**
         * Renders the full name display, toggling between a read view and an inline edit form.
         *
         * @param name The current full name of the user.
         * @returns The rendered full name section.
         */
        renderFullnameDisplay(name) {
            /**
             * Submits the updated full name to the server and closes the modal on success.
             *
             * @param mo The modal controller used to close the modal after a successful save.
             * @returns The action promise.
             */
            const submit = async (mo) => {
                const res = await UserStore.updateUserInfo({
                    name: this.fullNameInputValue,
                    newPw: undefined,
                    oldPw: undefined,
                });
                return res.addCallbacks({
                    onAfterSuccess: () => mo.close(),
                });
            };
            const modal = (<Modal class="contents" scopedSlots={{
                    trigger: mo => (<Button data-cy="edit-full-name" semantic="actionable" prominence={1} buttonClasses={[clsActionButton, { hidden: !this.canEditInfo }]} action={() => {
                            this.fullNameInputValue = name.orDefault('');
                            mo.open();
                        }}>
                                <Icon icon={icons.Edit}/>
                                <span class="sr-only">edit full name</span>
                            </Button>),
                    modalHeader: always('Change name'),
                    modalBody: mo => (<Form.Root data-cy="change-full-name" class="md:min-w-[75dvw]" handleSubmit={() => submit(mo)} submitLabel="Save" submitLoadingMessage="Saving full name" submitSuccessMessage="Full name saved" submitOnCtrlOrCommandEnter submitOnEnter>
                                <Form.Field class="contents" label="Full name">
                                    <Form.Input data-cy="user-full-name" class="title-medium h-9 w-full" value={this.fullNameInputValue} onInput={newVal => {
                            this.fullNameInputValue = newVal;
                        }} required v-focus/>
                                </Form.Field>
                            </Form.Root>),
                }}/>);
            return [
                <dt class="sr-only">User Full name</dt>,
                <dd class="contents title-medium text-brand-80 dark:text-brand-10 m-0">
                    <div data-cy="user-full-name" class="flex items-center gap-1 py-[3px]">
                        <span class={{ italic: name.isNothing() }}>
                            {name.orDefault('Full name not available')}
                        </span>

                        {modal}
                    </div>
                </dd>,
            ];
        },
        /**
         * Renders the current password input field used in the change-password and change-email forms.
         *
         * @param options Optional options for the field.
         * @param options.description - An optional description shown below the field label.
         * @returns The rendered current-password form field.
         */
        renderCurrentPasswordNode({ description, autofocus = false, } = {}) {
            return (<Form.Field label="Current password" description={description}>
                    <Form.Input data-cy="current-password" type="password" value={this.oldPasswordInputValue} onInput={newVal => {
                    this.oldPasswordInputValue = newVal;
                }} v-focus={autofocus}/>
                </Form.Field>);
        },
    },
    /** Ensures the logged-in user's data is fully loaded when the component mounts. */
    async mounted() {
        try {
            await UserStore.loggedInUser()
                .filter(u => u.fullyLoaded)
                .orDefaultLazy(() => UserStore.verifyLogin());
        }
        catch (error) {
            this.error = Maybe.of(errors.coerceToError(error));
        }
    },
    /** @returns The rendered user info card JSX. */
    render() {
        return (<Card header="User profile" class="user-info" bodyClass="@container">
                {this.error.caseOf({
                Just: err => <CgError error={err}/>,
                Nothing: () => this.bodyNode,
            })}
            </Card>);
    },
});
